import React, { useState } from "react";
import GuestDetailsModal from "../../Modals/guestDetailsModal/GuestDetailsModal";
import "./RefundList.css";
import { formatDateWithOrdinal, formatDateTime } from "../../helper/utils";

const RefundList = ({
  refunds = [],
  role,
  onApprove,
  onCancelRefund,
  searchTerm = "",
  onDecline,
}) => {
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [currentPage, setCurrentPage] = useState(1);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedRefund, setSelectedRefund] = useState(null);
  const isHost = role.includes("host");

  const normalizedRefunds = refunds.map((item) => {
    const displayUser = isHost
      ? item.guestDetails
      : {
          firstName: item.hostDetails?.hostFirstName,
          lastName: item.hostDetails?.hostLastName,
          phoneNumber: item.hostDetails?.hostPhoneNumber,
          email: item.hostDetails?.hostEmail,
          profileImage: item.hostDetails?.hostProfileImage,
        };

    const attires =
      item.refundDetails?.eventAttire?.map((a) => a.attireTag) || [];

    const attireLabel =
      attires.length === 0
        ? "Refund Request"
        : attires.length === 1
        ? attires[0]
        : attires.length === 2
        ? `${attires[0]} & ${attires[1]}`
        : attires.length > 2
        ? `${attires[0]}, ${attires[1]} & ${attires.length - 2} more`
        : `${attires.join(", ")}`;

    const totalRefundFormatted = `${item.refundDetails?.currency} ${item.refundDetails?.totalAmountToRefund}`;

    const refundDeadline = item.eventDetails?.eventAttire?.refundDeadlineDate
      ? formatDateWithOrdinal(item.eventDetails.eventAttire.refundDeadlineDate)
      : null;

    return {
      id: item.refundDetails?.refundId,
      name: `${displayUser?.firstName || ""} ${displayUser?.lastName || ""}`,
      phone: displayUser?.phoneNumber || "N/A",
      email: displayUser?.email || "N/A",
      userPhoto: displayUser?.profileImage || "/images/avatar.webp",

      eventTitle: item.eventDetails?.eventTitle,
      eventDate: item.eventDetails?.eventDate,

      attireList: attires,
      type: attireLabel,
      amount: item.refundDetails?.totalAmountToRefund,
      currency: item.refundDetails?.currency,

      status: item.refundDetails?.refundStatus,
      time: item.refundDetails?.requestDate
        ? formatDateTime(item.refundDetails?.requestDate)
        : "N/A",

      raw: item,
      totalRefundFormatted,
      refundDeadline,
    };
  });

  const filteredRefunds = normalizedRefunds.filter(
    (r) =>
      r.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      r.type.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const indexOfLast = currentPage * rowsPerPage;
  const indexOfFirst = indexOfLast - rowsPerPage;
  const currentRefunds = filteredRefunds.slice(indexOfFirst, indexOfLast);

  const totalPages = Math.ceil(filteredRefunds.length / rowsPerPage);

  const handleRefundClick = (refund) => {
    const raw = refund.raw;
    setSelectedRefund(raw);

    setIsModalOpen(true);
  };

  const getRefundStatusUI = (status) => {
    switch (status) {
      case "accepted":
        return { label: "Approved", className: "approved" };
      case "declined":
        return { label: "Rejected", className: "rejected" };
      case "cancelled":
        return { label: "Cancelled", className: "cancelled" };
      case "pending":
        return { label: "Pending", className: "pending" };
      default:
        return { label: "Pending", className: "pending" };
    }
  };

  return (
    <div className="refund-list-container">
      <div className="refund-list-list">
        {currentRefunds.length === 0 ? (
          <div className="refund-list-no-refunds-message">
            {searchTerm
              ? "No refunds found matching your search."
              : "No refunds found"}
          </div>
        ) : (
          currentRefunds.map((refund) => (
            <div
              key={refund.id}
              className="refund-list-item"
              onClick={() => handleRefundClick(refund)}
              style={{ cursor: "pointer" }}
            >
              <div className="refund-list-info">
                <div className="refund-list-avatar">
                  <img
                    src={refund.userPhoto}
                    alt={refund.name}
                    onError={(e) => (e.target.src = "/images/avatar.webp")}
                  />
                </div>

                <div className="refund-list-details">
                  <h3>{refund.name || "Unknown Guest"}</h3>
                  {refund.email && refund.email !== "N/A" && (
                    <p className="refund-list-email">{refund.email}</p>
                  )}
                  <p className="refund-list-phone">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"
                        fill="currentColor"
                      />
                    </svg>
                    {refund.phone}
                  </p>
                </div>
              </div>
              <div className="refund-list-actions">
                {(() => {
                  const ui = getRefundStatusUI(refund.status);
                  return (
                    <>
                      <span className={`refund-status-badge ${ui.className}`}>
                        {ui.label}
                      </span>
                      <span className="refund-list-badge refund-list-type refund-type-badge">
                        {refund.type}
                      </span>

                      <div className="refund-list-meta-inline">
                        <span className="refund-list-amount">
                          Refund of{" "}
                          <strong>{refund.totalRefundFormatted}</strong>
                        </span>

                        {refund.refundDeadline && (
                          <span className="refund-list-deadline">
                            Deadline: {refund.refundDeadline}
                          </span>
                        )}
                      </div>
                    </>
                  );
                })()}
              </div>
            </div>
          ))
        )}
      </div>

      {filteredRefunds.length > 0 && (
        <div className="refund-list-pagination">
          <span>
            {indexOfFirst + 1}–{Math.min(indexOfLast, filteredRefunds.length)}{" "}
            of {filteredRefunds.length}
          </span>

          <div className="refund-list-pagination-buttons">
            <button
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((p) => p - 1)}
            >
              Previous
            </button>
            <button
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage((p) => p + 1)}
            >
              Next
            </button>
          </div>
        </div>
      )}

      {selectedRefund && (
        <GuestDetailsModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onCancelRefund={() =>
            onCancelRefund({
              eventId: selectedRefund.eventDetails.event_id,
              refundId: selectedRefund.refundDetails.refundId,
            })
          }
          guestDetails={selectedRefund}
          refundDetails={selectedRefund.refundDetails}
          hideRefundActions={!isHost}
          showRSVP={false}
          onApprove={() => {
            onApprove({
              eventId: selectedRefund.eventDetails.event_id,
              refundId: selectedRefund.refundDetails.refundId,
              action: "approved",
            });
          }}
          onDecline={() => {
            onDecline({
              eventId: selectedRefund.eventDetails.event_id,
              refundId: selectedRefund.refundDetails.refundId,
              action: "declined",
            });
          }}
          userRole={role}
          isHistoryTab={false}
        />
      )}
    </div>
  );
};

export default RefundList;
